import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { getUsageStats } from '../../api/llmUsage.api';

const FREE_TIER_PROVIDERS = {
  groq: { name: 'Groq', daily: { requests: 14400 }, monthly: { requests: 432000 } },
  huggingface: { name: 'Hugging Face', monthly: { credits: 0.10 } },
  together: { name: 'Together AI', monthly: { credits: 1.0 } },
  cohere: { name: 'Cohere', monthly: { requests: 1000 } },
  mistral: { name: 'Mistral AI', daily: { requests: 100 }, monthly: { requests: 3000 } },
  deepseek: { name: 'DeepSeek', daily: { requests: 1000 }, monthly: { requests: 30000 } },
};

function UsageDashboard({ darkMode }) {
  const theme = useTheme();
  const [usage, setUsage] = useState({ daily: {}, monthly: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUsage();
    // Refresh every 30 seconds
    const interval = setInterval(loadUsage, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadUsage = async () => {
    try {
      setLoading(true);
      const { data, error: err } = await getUsageStats();
      if (err) {
        setError(err);
      } else {
        setUsage(data?.usage || { daily: {}, monthly: {} });
        setError(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getUsagePercentage = (used, limit) => {
    if (!limit) return 0;
    return Math.min(100, (used / limit) * 100);
  };

  const getStatusColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'success';
  };

  const formatResetTime = (resetAt) => {
    if (!resetAt) return 'N/A';
    const date = new Date(resetAt);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load usage statistics: {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        LLM Usage Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track your usage across free tier LLM providers to stay within limits
      </Typography>

      <Grid container spacing={3}>
        {Object.entries(FREE_TIER_PROVIDERS).map(([providerKey, providerInfo]) => {
          const dailyUsage = usage.daily?.[providerKey];
          const monthlyUsage = usage.monthly?.[providerKey];

          return (
            <Grid item xs={12} md={6} key={providerKey}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {providerInfo.name}
                    </Typography>
                    {dailyUsage && (
                      <Chip
                        label={dailyUsage.remaining > 0 ? 'Available' : 'Limit Reached'}
                        color={getStatusColor(getUsagePercentage(dailyUsage.used, dailyUsage.limit))}
                        size="small"
                        icon={dailyUsage.remaining > 0 ? <CheckCircleIcon /> : <WarningIcon />}
                      />
                    )}
                  </Box>

                  {/* Daily Usage */}
                  {dailyUsage && providerInfo.daily && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Daily Usage
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {dailyUsage.used} / {dailyUsage.limit} {dailyUsage.limitType || 'requests'}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={getUsagePercentage(dailyUsage.used, dailyUsage.limit)}
                        color={getStatusColor(getUsagePercentage(dailyUsage.used, dailyUsage.limit))}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Remaining: {dailyUsage.remaining}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Resets: {formatResetTime(dailyUsage.resetAt)}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Monthly Usage */}
                  {monthlyUsage && providerInfo.monthly && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Monthly Usage
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {monthlyUsage.used} / {monthlyUsage.limit} {monthlyUsage.limitType || 'requests'}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={getUsagePercentage(monthlyUsage.used, monthlyUsage.limit)}
                        color={getStatusColor(getUsagePercentage(monthlyUsage.used, monthlyUsage.limit))}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Remaining: {monthlyUsage.remaining}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Resets: {formatResetTime(monthlyUsage.resetAt)}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {!dailyUsage && !monthlyUsage && (
                    <Typography variant="body2" color="text.secondary">
                      No usage data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default UsageDashboard;
